import React from 'react'
import LegalLayout from '../../components/LegalLayout'
import PolicyBody from '../../components/PolicyBody'

/**
 * 개인정보처리방침. URL: /privacy-policy
 *
 * 내용은 서버(policies 표)에서 읽는다. 관리자 화면(/admin/policies)에서 고치면
 * 배포 없이 반영된다.
 *
 * 아래 본문은 서버를 못 읽었을 때 쓰는 대비본이다. 법적 고지라 화면이 비어
 * 있으면 안 되므로 남겨둔다. 다만 두 벌이 갈라지기 쉬우므로, 방침을 고칠 때는
 * 서버 쪽을 고치는 것이 원칙이다.
 */
const PrivacyPolicyPage: React.FC = () => {
  return (
    <LegalLayout>
      <PolicyBody policyKey="privacy" fallback={<LegacyBody />} />
    </LegalLayout>
  )
}

/** 서버를 못 읽었을 때 보여줄 본문 */
const LegacyBody: React.FC = () => {
  return (
    <>
      <h1>개인정보처리방침</h1>
      <p className="legal-eff">
        시행일: 2026년 1월 1일
        <br />
        최종 수정일: 2026년 7월 28일
      </p>

      <p>
        TASTEE는 「개인정보 보호법」, 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」
        등 관련 법령에 따라 이용자의 개인정보를 보호하고 이와 관련한 고충을 신속하고
        원활하게 처리할 수 있도록 다음과 같이 개인정보처리방침을 수립·공개합니다.
      </p>

      <h2>1. 개인정보의 처리목적</h2>
      <p>
        TASTEE는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는
        다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는
        개인정보보호법 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할
        예정입니다.
      </p>

      <h3>1.1 서비스 제공</h3>
      <ul className="bullets">
        <li>음식점 등 방문 기록 작성·보관 및 친구 간 공유 서비스 제공</li>
        <li>회원가입 및 회원제 서비스 이용에 따른 본인확인</li>
        <li>연락처 대조를 통한 친구 찾기 기능 제공</li>
        <li>서비스 이용기록과 접속빈도 분석, 서비스 이용에 대한 통계</li>
      </ul>

      <h3>1.2 회원 관리</h3>
      <ul className="bullets">
        <li>
          회원 식별, 가입의사 확인, 만 14세 미만 여부 확인, 가입 및 가입횟수 제한
        </li>
        <li>고충처리, 분쟁조정을 위한 기록 보존</li>
        <li>공지사항 전달, 불만처리 등을 위한 원활한 의사소통 경로의 확보</li>
      </ul>

      <h3>1.3 마케팅·광고 (별도 동의 시)</h3>
      <ul>
        <li>신규 기능, 이벤트, 프로모션 등 광고성 정보 안내</li>
      </ul>

      <h2>2. 개인정보의 처리 및 보유기간</h2>
      <p>
        TASTEE는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를
        수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.
      </p>

      <h3>2.1 회원 정보</h3>
      <ul>
        <li>보유기간: 회원 탈퇴 시까지</li>
        <li>보유근거: 서비스 이용계약 (정보주체의 동의)</li>
      </ul>
      <p>
        회원 탈퇴 시 휴대폰번호, 이메일, 닉네임, 프로필 사진, 소개글 및 소셜
        로그인 연동 정보는 지체 없이 파기합니다. 계정 아이디는 타인의 사칭을
        방지하기 위해 탈퇴 후 14일간 보관한 뒤 파기합니다.
      </p>

      <h3>2.2 탈퇴 후 게시물의 처리</h3>
      <p>
        작성하신 기록과 댓글은 다른 이용자의 서비스 이용에 필요하므로 탈퇴
        후에도 익명 처리되어 유지됩니다. 작성자 정보는 식별할 수 없는 임의의
        이름으로 대체되며, 프로필 사진과 아이디는 표시되지 않습니다. 삭제를
        원하시는 기록은 탈퇴 전에 직접 삭제하실 수 있습니다.
      </p>

      <h3>2.3 관계 법령에 따른 보존</h3>
      <ul className="bullets">
        <li>서비스 방문 기록(접속 로그, 접속 IP): 3개월 (통신비밀보호법)</li>
        <li>
          소비자 불만 또는 분쟁처리에 관한 기록: 3년 (전자상거래 등에서의 소비자보호에
          관한 법률)
        </li>
      </ul>

      <h2>3. 처리하는 개인정보의 항목</h2>
      <p>TASTEE는 다음의 개인정보 항목을 처리하고 있습니다.</p>

      <h3>3.1 필수항목</h3>
      <ul className="bullets">
        <li>휴대전화번호</li>
        <li>닉네임, 아이디</li>
        <li>만 14세 이상 여부</li>
        <li>로그인 유형 (일반, 카카오, 네이버, 애플, 구글)</li>
        <li>
          소셜 로그인 이용 시: 해당 플랫폼에서 제공하는 회원 식별자 및 프로필
          정보(닉네임 등)
        </li>
      </ul>

      <h3>3.2 선택항목</h3>
      <ul className="bullets">
        <li>프로필 이미지</li>
        <li>
          기기 연락처(전화번호): 친구 찾기 기능 이용 시에만 수집하며, 오직 친구 찾기
          목적으로만 이용됩니다. 연락처에 포함된 비회원의 정보는 회원 대조 목적 외에
          저장·이용하지 않습니다.
        </li>
      </ul>

      <h3>3.3 자동 수집 항목</h3>
      <ul className="bullets">
        <li>서비스 이용기록</li>
        <li>접속 IP 정보</li>
        <li>접속 로그</li>
        <li>기기정보(OS 종류·버전, 기기 모델명, 앱 버전)</li>
        <li>푸시 토큰</li>
        <li>
          위치정보(현재 위치 좌표): 위치 권한 허용 시 주변 장소 추천 및 검색 결과
          정렬에 이용되며, 요청 처리 후 저장하지 않습니다.
        </li>
      </ul>

      <h3>3.4 기기 접근 권한을 통해 기기 내에서만 처리되는 정보</h3>
      <ul>
        <li>
          사진(갤러리) 접근: 리뷰 사진 첨부 및 방문한 장소 근처에서 촬영한 사진 추천
          기능을 위해 기기의 사진 라이브러리 접근 권한(선택)을 요청합니다. 사진에
          포함된 위치 정보(EXIF GPS)는 이용자의 기기 내에서만 장소와의 거리 계산에
          사용되며, 이용자가 직접 첨부를 선택한 사진 외에는 회사 서버로
          전송·수집·저장되지 않습니다. 권한을 거부해도 사진 추천 기능 외 서비스
          이용에는 제한이 없습니다.
        </li>
      </ul>

      <h2>4. 개인정보의 제3자 제공</h2>
      <p>
        TASTEE는 정보주체의 개인정보를 제1조(개인정보의 처리목적)에서 명시한 범위
        내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 개인정보 보호법
        제17조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.
      </p>

      <h2>5. 개인정보처리의 위탁</h2>
      <p>
        TASTEE는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를
        위탁하고 있습니다.
      </p>

      <h3>5.1 위탁받는 자 (수탁자)</h3>
      <ul>
        <li>Amazon Web Services, Inc.: 서비스 인프라 운영 및 데이터 보관 (서울 리전)</li>
        <li>주식회사 넥스트페이먼츠(솔라피): 본인 인증 문자메시지(SMS) 발송</li>
        <li>Google LLC: Firebase 푸시 알림(FCM) 발송, 구글 로그인</li>
        <li>
          주식회사 카카오: 카카오 로그인, 장소 검색 (검색 시 근접 결과 제공을 위해
          위치 좌표가 전달되며 저장되지 않습니다)
        </li>
        <li>네이버 주식회사: 네이버 로그인</li>
        <li>Apple Inc.: 애플 로그인</li>
      </ul>

      <h3>5.2 개인정보의 국외 이전</h3>
      <p>
        푸시 알림 발송 과정에서 다음과 같이 개인정보가 국외로 이전될 수 있습니다.
      </p>
      <ul>
        <li>이전받는 자: Google LLC (Firebase Cloud Messaging)</li>
        <li>이전되는 국가: 미국</li>
        <li>이전 항목: 푸시 토큰, 기기정보</li>
        <li>이전 방법 및 시기: 알림 발송 시 네트워크를 통한 전송</li>
        <li>
          이용 목적 및 보유기간: 푸시 알림 발송 / 회원 탈퇴 또는 알림 기능 이용 중단
          시까지
        </li>
      </ul>
      <p>
        이용자는 기기의 알림 권한을 허용하지 않음으로써 국외 이전을 거부할 수 있으며,
        이 경우 푸시 알림 기능 이용이 제한됩니다.
      </p>

      <h2>6. 정보주체의 권리·의무 및 행사방법</h2>
      <p>
        정보주체는 TASTEE에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를
        행사할 수 있습니다.
      </p>
      <ul className="bullets">
        <li>개인정보 처리현황 통지요구</li>
        <li>개인정보 열람요구</li>
        <li>개인정보 정정·삭제요구</li>
        <li>개인정보 처리정지 요구</li>
      </ul>
      <p>
        권리 행사는 앱 내 설정 메뉴 또는 제9조의 개인정보 보호책임자에게 전자우편으로
        요청할 수 있으며, TASTEE는 지체 없이 조치합니다.
      </p>
      <p>
        TASTEE는 만 14세 미만 아동의 개인정보를 수집하지 않습니다. 만 14세 미만 아동이
        가입한 사실이 확인되는 경우 해당 계정과 개인정보를 지체 없이 삭제합니다.
      </p>

      <h2>7. 개인정보의 파기 절차 및 방법</h2>
      <p>
        TASTEE는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게
        되었을 때에는 지체 없이 해당 개인정보를 파기합니다. 전자적 파일 형태의 정보는
        복구할 수 없는 기술적 방법으로 삭제합니다.
      </p>

      <h2>8. 개인정보의 안전성 확보조치</h2>
      <p>
        TASTEE는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.
      </p>
      <ul className="bullets">
        <li>관리적 조치: 개인정보 취급 인원의 최소화, 내부관리계획 수립·시행</li>
        <li>
          기술적 조치: 개인정보처리시스템 등의 접근권한 관리, 접근통제시스템 설치,
          전송 구간 암호화(HTTPS), 주요 정보의 암호화 저장, 보안프로그램 설치 및
          주기적 갱신·점검
        </li>
        <li>물리적 조치: 클라우드 인프라 접근 통제(접근 키 관리 등)</li>
      </ul>

      <h2>9. 개인정보 보호책임자</h2>
      <p>개인정보 보호책임자</p>
      <ul>
        <li>성명: 김경민</li>
        <li>직책: 대표</li>
        <li>
          연락처: <a href="mailto:admin@tastee.kr">admin@tastee.kr</a>
        </li>
      </ul>
      <p>
        정보주체는 서비스 이용 중 발생한 모든 개인정보 보호 관련 문의, 불만 처리, 피해
        구제 등을 개인정보 보호책임자에게 문의할 수 있습니다.
      </p>
      <p>
        기타 개인정보 침해에 대한 신고나 상담이 필요한 경우에는 아래 기관에 문의할 수
        있습니다.
      </p>
      <ul>
        <li>개인정보침해신고센터 (privacy.kisa.or.kr / 국번 없이 118)</li>
        <li>개인정보분쟁조정위원회 (kopico.go.kr / 1833-6972)</li>
        <li>경찰청 사이버수사국 (ecrm.police.go.kr / 국번 없이 182)</li>
      </ul>

      <h2>10. 개인정보처리방침의 변경</h2>
      <p>
        이 개인정보처리방침은 시행일로부터 적용되며, 법령 및 방침에 따른 변경내용의
        추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여
        고지할 것입니다.
      </p>
      <p>
        <strong>본 개인정보처리방침은 2026년 1월 1일부터 시행됩니다.</strong>
      </p>
    </>
  )
}

export default PrivacyPolicyPage
